// Drawer displaying Relationship intelligence for a document, laid out after
// the Relationship Layer design: header tags, status banners, layer summary,
// cited documents, the three relationship tables and the document graph.
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import type { RelationshipResult } from "./utilities/types";
import {
  REL_COLOR,
  RELATIONSHIP_COPY,
  RELATIONSHIP_DRAWER_WIDTH_CLS,
  RELATIONSHIP_SECTIONS,
} from "./utilities/constants";
import {
  buildRelationshipHeaderState,
  formatEvaluatedDate,
  pluralSuffix,
  type RelationshipHeaderState,
} from "./utilities/helpers";
import {
  RelBanner,
  RelCard,
  RelKeyframes,
  RelationshipHeader,
  RelationshipLoading,
  StatTiles,
} from "./parts";
import {
  CitedDocumentsSection,
  CrossDocumentSection,
  SemanticSection,
  WithinDocumentSection,
} from "./relationshipSections";
import { RelationshipGraphSection } from "./relationshipGraph";

export interface RelationshipIntelligenceDrawerProps {
  open: boolean;
  onClose: () => void;
  result?: RelationshipResult | null;
  documentTitle?: string;
  documentType?: string;
}

export function RelationshipIntelligenceDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
}: RelationshipIntelligenceDrawerProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        overlayClassName="z-[80]"
        // Keep focus on the sheet itself instead of the first filter pill.
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={`z-[80] flex h-full ${RELATIONSHIP_DRAWER_WIDTH_CLS} flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[95vw]`}
      >
        <SheetTitle className="sr-only">{RELATIONSHIP_COPY.title}</SheetTitle>
        <SheetDescription className="sr-only">
          {RELATIONSHIP_COPY.sheetDescription}
        </SheetDescription>
        {!result ? (
          <RelationshipLoading />
        ) : (
          <RelationshipContent
            key={result.documentId ?? result.document?.document_id ?? "no-doc"}
            data={result}
            documentTitle={documentTitle}
            documentType={documentType}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function RelationshipContent({
  data,
  documentTitle,
  documentType,
}: {
  data: RelationshipResult;
  documentTitle?: string;
  documentType?: string;
}) {
  const state = buildRelationshipHeaderState(data, documentTitle, documentType);

  return (
    <>
      <RelKeyframes />
      <RelationshipHeader
        documentTitle={state.title}
        statusLabel={state.layerStatus}
        statusTone={state.headerStatusTone}
        tags={state.headerTags}
      />

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-background p-4 sm:px-6 sm:py-5">
        {state.hasConflicts && <ContradictionBanner state={state} />}

        <SummaryBanner state={state} />

        <RelCard
          title={RELATIONSHIP_SECTIONS.summary.title}
          dot={RELATIONSHIP_SECTIONS.summary.dot}
          right={`status ${state.layerStatus.toLowerCase()} · evaluated ${formatEvaluatedDate(data.createdAt)}`}
        >
          <StatTiles tiles={buildSummaryTiles(state)} />
        </RelCard>

        <CitedDocumentsSection docs={state.view.citedDocs} kind={state.kindLower} />
        <CrossDocumentSection rows={state.view.cross} resolution={state.view.resolution} />
        <WithinDocumentSection rows={state.view.internal} methods={state.view.methods} />
        <SemanticSection rows={state.view.semantic} detectionLabel={state.relDetectionLabel} />
        <RelationshipGraphSection
          docs={state.view.citedDocs}
          citations={state.view.cross}
          documentTitle={state.title}
        />
      </div>
    </>
  );
}

function ContradictionBanner({ state }: { state: RelationshipHeaderState }) {
  const { contradictions, duplicates } = state.view;
  return (
    <RelBanner tone="red">
      <b>
        {contradictions} pair{pluralSuffix(contradictions)} of clauses in this{" "}
        {state.kindLower} cannot both be satisfied.
      </b>{" "}
      {duplicates} further pair{pluralSuffix(duplicates)} state the same
      obligation twice. Listed first under Semantic Relationships.
    </RelBanner>
  );
}

function SummaryBanner({ state }: { state: RelationshipHeaderState }) {
  const { nCit, nInt, nSem, allUnresolved } = state;
  return (
    <RelBanner tone={state.mainTone}>
      {nCit} cross-document citation{pluralSuffix(nCit)} · {nInt}{" "}
      within-document reference{pluralSuffix(nInt)} · {nSem} semantic
      relationship{pluralSuffix(nSem)}. Citation resolution{" "}
      <b>{state.view.resolution}</b>
      {allUnresolved ? `, so all ${nCit} citations are unresolved.` : "."}
    </RelBanner>
  );
}

function buildSummaryTiles(state: RelationshipHeaderState) {
  return [
    { label: "Total edges", value: state.view.edges, color: REL_COLOR.accent },
    { label: "Cross-document citations", value: state.nCit, color: REL_COLOR.cyan },
    { label: "Within-document references", value: state.nInt, color: REL_COLOR.purple },
    { label: "Semantic relationships", value: state.nSem, color: REL_COLOR.pink },
    { label: "Contradictions", value: state.view.contradictions, color: REL_COLOR.red },
  ];
}
